/**
 * The aura reading, ported from the prototype's aura engine.
 *
 * **Nothing here looks at a photograph.** Every number is computed from what
 * the member says about their day, their biorhythm cycles, the element of
 * their chart's reference sign, whether they have an active goal, and how much
 * they have actually practised this week. The prototype said the same thing in
 * its own first comment, and it is the only reason a feature like this can
 * ship honestly: it is a mirror of the answers given, not a scan of anybody.
 *
 * Pure, so it lives here beside the Success Score: the API gathers the inputs
 * and this decides. Every score is a reflection index on a 0-100 scale, never
 * a measurement of a body, a mood or a health state.
 */

export const COACHING_AURA_DISCLAIMER =
  'This reading is computed from the answers you just gave, your cycles and your practice this week. Nothing is scanned or measured, and none of it describes your health.';

export const AURA_COLOR_KEYS = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'indigo',
  'violet',
  'pink',
  'gold',
  'white',
  'turquoise',
  'silver',
] as const;
export type AuraColorKey = (typeof AURA_COLOR_KEYS)[number];

export type AuraColorDef = { key: AuraColorKey; name: string; hex: string; theme: string };

export const AURA_COLORS: readonly AuraColorDef[] = [
  { key: 'red', name: 'Red', hex: '#EF4444', theme: 'Vitality, drive, getting something moving' },
  {
    key: 'orange',
    name: 'Orange',
    hex: '#F97316',
    theme: 'Creativity, appetite, enjoying the work',
  },
  {
    key: 'yellow',
    name: 'Yellow',
    hex: '#EAB308',
    theme: 'Confidence, clear thinking, personal authority',
  },
  {
    key: 'green',
    name: 'Green',
    hex: '#22C55E',
    theme: 'Balance, patience, care for other people',
  },
  { key: 'blue', name: 'Blue', hex: '#3B82F6', theme: 'Calm, honesty, saying the thing plainly' },
  {
    key: 'indigo',
    name: 'Indigo',
    hex: '#6366F1',
    theme: 'Perception, reading a situation, inner quiet',
  },
  { key: 'violet', name: 'Violet', hex: '#A855F7', theme: 'Practice, meaning, the longer view' },
  {
    key: 'pink',
    name: 'Pink',
    hex: '#EC4899',
    theme: 'Warmth, gentleness with yourself and others',
  },
  {
    key: 'gold',
    name: 'Gold',
    hex: '#B08D3F',
    theme: 'Abundance, ambition, finishing what was started',
  },
  {
    key: 'white',
    name: 'White',
    hex: '#E8E4DC',
    theme: 'Integration: nothing pulling too hard in one direction',
  },
  {
    key: 'turquoise',
    name: 'Turquoise',
    hex: '#14B8A6',
    theme: 'Expressive calm, creative communication',
  },
  {
    key: 'silver',
    name: 'Silver',
    hex: '#94A3B8',
    theme: 'Potential, transition, a sensitivity to change',
  },
];

export const AURA_STRENGTHS = ['Supporting', 'Moderate', 'Strong', 'Dominant'] as const;
export type AuraStrength = (typeof AURA_STRENGTHS)[number];

export const AURA_ELEMENTS = ['fire', 'earth', 'air', 'water', 'mixed'] as const;
export type AuraElement = (typeof AURA_ELEMENTS)[number];

export const AURA_CHAKRAS = [
  'root',
  'sacral',
  'solarPlexus',
  'heart',
  'throat',
  'thirdEye',
  'crown',
] as const;
export type AuraChakra = (typeof AURA_CHAKRAS)[number];

export const AURA_CHAKRA_LABELS: Record<AuraChakra, string> = {
  root: 'Root',
  sacral: 'Sacral',
  solarPlexus: 'Solar plexus',
  heart: 'Heart',
  throat: 'Throat',
  thirdEye: 'Third eye',
  crown: 'Crown',
};

/** What each centre is traditionally about, said as a theme rather than a body part. */
export const AURA_CHAKRA_THEMES: Record<AuraChakra, string> = {
  root: 'Steadiness and the basics being handled',
  sacral: 'Feeling, appetite and creative energy',
  solarPlexus: 'Confidence and the will to act',
  heart: 'Warmth toward yourself and other people',
  throat: 'Saying what is true, plainly',
  thirdEye: 'Perception and quiet judgement',
  crown: 'Meaning and the longer view',
};

export const AURA_DIMENSIONS = ['manifestation', 'abundance', 'emotional', 'spiritual'] as const;
export type AuraDimension = (typeof AURA_DIMENSIONS)[number];

export const AURA_DIMENSION_LABELS: Record<AuraDimension, string> = {
  manifestation: 'Manifestation',
  abundance: 'Abundance',
  emotional: 'Emotional',
  spiritual: 'Spiritual',
};

/** The five things the member is asked, each 1-5. */
export const AURA_CHECKIN_KEYS = ['mood', 'energy', 'confidence', 'stress', 'focus'] as const;
export type AuraCheckInKey = (typeof AURA_CHECKIN_KEYS)[number];
export type AuraCheckIn = Record<AuraCheckInKey, number>;

export const AURA_CHECKIN_PROMPTS: Record<
  AuraCheckInKey,
  { label: string; low: string; high: string }
> = {
  mood: { label: 'Mood', low: 'Low', high: 'Bright' },
  energy: { label: 'Energy', low: 'Drained', high: 'Vibrant' },
  confidence: { label: 'Confidence', low: 'Uncertain', high: 'Sure' },
  stress: { label: 'Stress', low: 'Calm', high: 'Wound up' },
  focus: { label: 'Focus', low: 'Scattered', high: 'Sharp' },
};

export type AuraInputs = {
  checkIn: AuraCheckIn;
  /** Each 0-1: where the cycle sits between its low and its high. */
  biorhythm: { physical: number; emotional: number; intellectual: number; spiritual: number };
  element: AuraElement;
  hasActiveGoal: boolean;
  /** 0-1, from practices actually recorded in the last seven days. */
  recentPractice: number;
};

export type AuraColorReading = AuraColorDef & { strength: AuraStrength; score: number };

export type AuraReading = {
  colors: { primary: AuraColorReading; secondary: AuraColorReading; supporting: AuraColorReading };
  /** 0-100. An index of the answers given, not a measurement. */
  strength: number;
  chakras: Record<AuraChakra, number>;
  dimensions: Record<AuraDimension, number>;
  scores: Record<AuraColorKey, number>;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
/** A 1-5 answer as 0-1. */
const norm15 = (v: number) => clamp01((v - 1) / 4);
/** Stress is asked as stress and used as calm, so a high answer lowers the score. */
const calmness = (stress: number) => clamp01((5 - stress) / 4);

const elementMatch = (target: AuraElement, actual: AuraElement): number => {
  if (actual === target) return 1;
  if (actual === 'mixed') return 0.5;
  return 0.15;
};

function variance(values: number[]): number {
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const v = values.reduce((s, x) => s + (x - avg) ** 2, 0) / values.length;
  return Math.min(1, v * 4);
}

function normalized(checkIn: AuraCheckIn) {
  return {
    mood: norm15(checkIn.mood),
    energy: norm15(checkIn.energy),
    confidence: norm15(checkIn.confidence),
    calm: calmness(checkIn.stress),
    focus: norm15(checkIn.focus),
  };
}

/**
 * Each colour is a weighted mix of the check-in, the cycles, the chart's
 * element and what the member has practised. The weights are the prototype's,
 * which it called illustrative and tunable; they are kept as-is so a reading
 * taken in the prototype and one taken here agree.
 */
export function auraColorScores(inputs: AuraInputs): Record<AuraColorKey, number> {
  const c = normalized(inputs.checkIn);
  const b = inputs.biorhythm;
  const el = inputs.element;
  const goal = inputs.hasActiveGoal ? 1 : 0.3;
  const practice = 0.3 + clamp01(inputs.recentPractice) * 0.7;
  const balance = 1 - variance([c.mood, c.energy, c.confidence, c.calm, c.focus]);
  const bioAvg = (b.physical + b.emotional + b.intellectual + b.spiritual) / 4;

  return {
    red:
      0.3 * c.energy +
      0.25 * b.physical +
      0.2 * elementMatch('fire', el) +
      0.15 * c.confidence +
      0.1 * goal,
    orange:
      0.3 * c.mood +
      0.25 * b.emotional +
      0.2 * elementMatch('water', el) +
      0.15 * c.calm +
      0.1 * practice,
    yellow:
      0.3 * c.confidence +
      0.25 * b.intellectual +
      0.2 * elementMatch('fire', el) +
      0.15 * c.focus +
      0.1 * goal,
    green:
      0.3 * c.calm +
      0.25 * c.mood +
      0.2 * elementMatch('earth', el) +
      0.15 * practice +
      0.1 * b.emotional,
    blue:
      0.3 * c.calm +
      0.25 * c.focus +
      0.2 * elementMatch('air', el) +
      0.15 * c.confidence +
      0.1 * practice,
    indigo:
      0.3 * b.spiritual +
      0.25 * practice +
      0.2 * elementMatch('air', el) +
      0.15 * c.focus +
      0.1 * c.mood,
    violet:
      0.35 * b.spiritual +
      0.25 * practice +
      0.2 * elementMatch('water', el) +
      0.1 * c.calm +
      0.1 * c.mood,
    pink:
      0.3 * c.mood +
      0.25 * b.emotional +
      0.2 * elementMatch('water', el) +
      0.15 * c.calm +
      0.1 * practice,
    gold:
      0.3 * c.confidence +
      0.25 * goal +
      0.2 * elementMatch('earth', el) +
      0.15 * c.focus +
      0.1 * c.energy,
    white: 0.25 * c.focus + 0.2 * c.calm + 0.2 * practice + 0.2 * bioAvg + 0.15 * balance,
    turquoise:
      0.25 * c.calm +
      0.2 * c.mood +
      0.2 * elementMatch('water', el) +
      0.2 * practice +
      0.15 * b.emotional,
    silver:
      0.3 * b.spiritual +
      0.2 * practice +
      0.2 * (1 - Math.abs(b.physical - 0.5) * 2) +
      0.15 * elementMatch('air', el) +
      0.15 * c.mood,
  };
}

export function auraStrengthLabel(score: number): AuraStrength {
  if (score >= 0.6) return 'Dominant';
  if (score >= 0.45) return 'Strong';
  if (score >= 0.3) return 'Moderate';
  return 'Supporting';
}

const pct = (v: number) => Math.round(clamp01(v) * 100);

export function auraChakras(inputs: AuraInputs): Record<AuraChakra, number> {
  const c = normalized(inputs.checkIn);
  const b = inputs.biorhythm;
  const goal = inputs.hasActiveGoal ? 1 : 0.3;
  const practice = 0.3 + clamp01(inputs.recentPractice) * 0.7;
  return {
    root: pct(0.45 * c.energy + 0.35 * b.physical + 0.2 * goal),
    sacral: pct(0.45 * c.mood + 0.35 * b.emotional + 0.2 * c.calm),
    solarPlexus: pct(0.45 * c.confidence + 0.35 * b.intellectual + 0.2 * c.focus),
    heart: pct(0.4 * c.mood + 0.3 * c.calm + 0.3 * practice),
    throat: pct(0.4 * c.confidence + 0.3 * c.calm + 0.3 * c.focus),
    thirdEye: pct(0.45 * b.spiritual + 0.3 * c.focus + 0.25 * practice),
    crown: pct(0.5 * b.spiritual + 0.3 * practice + 0.2 * c.calm),
  };
}

/** The whole reading. Deterministic: the same inputs always give the same answer. */
export function computeAura(inputs: AuraInputs): AuraReading {
  const scores = auraColorScores(inputs);
  const byKey = new Map(AURA_COLORS.map((c) => [c.key, c]));
  const ranked = (Object.entries(scores) as Array<[AuraColorKey, number]>).sort(
    // Ties break on the catalog order, so a reading never flips between two
    // equal colours from one call to the next.
    (a, b) => b[1] - a[1] || AURA_COLOR_KEYS.indexOf(a[0]) - AURA_COLOR_KEYS.indexOf(b[0]),
  );
  const reading = ([key, score]: [AuraColorKey, number]): AuraColorReading => ({
    ...byKey.get(key)!,
    strength: auraStrengthLabel(score),
    score,
  });

  const c = normalized(inputs.checkIn);
  const b = inputs.biorhythm;
  const bioAvg = (b.physical + b.emotional + b.intellectual + b.spiritual) / 4;
  const checkInAvg = (c.mood + c.energy + c.confidence + c.calm + c.focus) / 5;
  const practice = 0.3 + clamp01(inputs.recentPractice) * 0.7;
  const goal = inputs.hasActiveGoal ? 1 : 0.35;

  return {
    colors: {
      primary: reading(ranked[0]!),
      secondary: reading(ranked[1]!),
      supporting: reading(ranked[2]!),
    },
    strength: pct(0.45 * checkInAvg + 0.35 * bioAvg + 0.2 * practice),
    chakras: auraChakras(inputs),
    dimensions: {
      manifestation: pct(0.35 * scores.gold + 0.35 * scores.violet + 0.3 * goal),
      abundance: pct(0.55 * scores.gold + 0.25 * c.confidence + 0.2 * goal),
      emotional: pct(0.4 * scores.pink + 0.3 * scores.green + 0.3 * scores.turquoise),
      spiritual: pct(0.4 * scores.violet + 0.3 * scores.indigo + 0.3 * scores.silver),
    },
    scores,
  };
}
